import axiosInstance from "../axiosInstance"
import type {
  AdResponseDto,
  AdListParams,
  AdListResponseDto,
  DeleteAdResponseDto,
  AdPromotionHistoryDto,
  AdRenewalHistoryDto,
} from "../types/ad.types"

/**
 * Ad API Client
 * Direct API calls using axios instance
 */
export const adClient = {
  /**
   * Get ads feed with filters and pagination (admin endpoint)
   * @param params - Query parameters for filtering and pagination
   * @returns Promise with paginated list of ads (API returns {ads: [...]} or AdResponseDto[])
   */
  getAds: (params?: AdListParams): Promise<AdListResponseDto> => {
    // Filter out undefined values to avoid sending them to the API
    const apiParams: Record<string, string | number | boolean> = {}
    
    if (params) {
      if (params.adType !== undefined) {
        apiParams.adType = params.adType
      }
      if (params.city !== undefined && params.city !== "") {
        apiParams.city = params.city
      }
      if (params.priceMin !== undefined) {
        apiParams.priceMin = params.priceMin
      }
      if (params.priceMax !== undefined) {
        apiParams.priceMax = params.priceMax
      }
      if (params.brandId !== undefined && params.brandId !== "") {
        apiParams.brandId = params.brandId
      }
      if (params.modelId !== undefined && params.modelId !== "") {
        apiParams.modelId = params.modelId
      }
      if (params.motoType !== undefined) {
        apiParams.motoType = params.motoType
      }
      if (params.gearType !== undefined) {
        apiParams.gearType = params.gearType
      }
      if (params.condition !== undefined) {
        apiParams.condition = params.condition
      }
      if (params.yearMin !== undefined) {
        apiParams.yearMin = params.yearMin
      }
      if (params.yearMax !== undefined) {
        apiParams.yearMax = params.yearMax
      }
      if (params.topOnly !== undefined) {
        apiParams.topOnly = params.topOnly
      }
      if (params.urgentOnly !== undefined) {
        apiParams.urgentOnly = params.urgentOnly
      }
      if (params.highlightOnly !== undefined) {
        apiParams.highlightOnly = params.highlightOnly
      }
      if (params.cursor !== undefined && params.cursor !== "") {
        apiParams.cursor = params.cursor
      }
      if (params.limit !== undefined) {
        apiParams.limit = params.limit
      }
    }
    
    return axiosInstance.get("/ads/admin", { params: apiParams })
  },

  /**
   * Get single ad by ID
   * @param id - Ad ID (UUID)
   * @returns Promise with ad details
   */
  getAdById: (id: string): Promise<AdResponseDto> => {
    return axiosInstance.get(`/ads/${id}`)
  },

  /**
   * Delete ad by ID (admin only)
   * @param id - Ad ID (UUID)
   * @param description - Reason for deletion
   * @returns Promise with delete confirmation
   */
  deleteAd: (id: string, description: string): Promise<DeleteAdResponseDto> => {
    return axiosInstance.post(`/ads/${id}/delete`, {
      description,
    })
  },

  /**
   * Get ad promotion history (admin only)
   * @param id - Ad ID (UUID)
   * @returns Promise with promotion history
   */
  getAdPromotions: (id: string): Promise<AdPromotionHistoryDto[]> => {
    return axiosInstance.get(`/ads/admin/${id}/promotions`)
  },

  /**
   * Get ad renewal history (admin only)
   * @param id - Ad ID (UUID)
   * @returns Promise with renewal history
   */
  getAdRenewals: (id: string): Promise<AdRenewalHistoryDto[]> => {
    return axiosInstance.get(`/ads/admin/${id}/renewals`)
  },
}

